import { Flex, Text } from '@mantine/core';
import { Fragment, useCallback, useMemo } from 'react';
import {
  BORDER_COLOR,
  CELL_PADDING,
  FONT_SIZE,
  HEADER_BG,
  INDENT_SIZE,
  LEFT_SECTION_WIDTH,
  LINE_HEIGHT,
  TEXT_COLOR,
  useTableStyles
} from '../tableConstants';
import { getColumnEntries, parseQuestionDisplayName, resolveColumnValue } from '../tableUtils';
import { useCellNavigation } from '../useCellNavigation';
import { SingleFundHeaderRow } from '../components/SingleFundHeaderRow';
import { MultiFundRow } from '../components/MultiFundRow';
import { SingleFundRow } from '../components/SingleFundRow';
import { QuestionHeader } from '../components/QuestionHeader';
import { idCreator } from '../../../../../Project/utils/idCreator';

const MultiFundQuestion = ({
  question,
  schema,
  funds,
  fundKeys,
  tableName,
  levelIndex,
  handleCellChange,
  cellRefs,
  onCellKeyDown
}) => {
  const { columns, children, level, name } = question;
  const columnEntries = getColumnEntries(columns);
  const keyPrefix = question.level ?? question.name;
  const { classes } = useTableStyles();
  return (
    <>
      {(name || level) && (
        <Flex
          id={`formpf-simple-multi-strip-${idCreator(`${tableName}-${name || level}`)}`}
          className={classes.borderBottom}
        >
          <Flex
            id={`formpf-simple-multi-strip-left-${idCreator(`${tableName}-${name || level}`)}`}
            maw={LEFT_SECTION_WIDTH}
            miw={LEFT_SECTION_WIDTH}
            className={classes.leftSticky}
          >
            {level && (
              <Text
                id={`formpf-simple-multi-strip-level-${idCreator(`${tableName}-${name || level}`)}`}
                p={CELL_PADDING}
                bg={HEADER_BG}
                miw={`${INDENT_SIZE}px`}
                ta="center"
                c={TEXT_COLOR}
                fz={FONT_SIZE}
                lh={LINE_HEIGHT}
                className={classes.borderRight}
              >
                {level}
              </Text>
            )}
            <Text
              id={`formpf-simple-multi-strip-name-${idCreator(`${tableName}-${name || level}`)}`}
              p={CELL_PADDING}
              bg={HEADER_BG}
              flex="auto"
              c={TEXT_COLOR}
              fz={FONT_SIZE}
              lh={LINE_HEIGHT}
              className={classes.borderRight}
            >
              {name}
            </Text>
          </Flex>
          <Text
            id={`formpf-simple-multi-strip-right-${idCreator(`${tableName}-${name || level}`)}`}
            p={CELL_PADDING}
            bg={HEADER_BG}
            flex="auto"
          ></Text>
        </Flex>
      )}
      {!children &&
        columnEntries.map(([columnKey, columnSchema], index) => (
          <MultiFundRow
            key={`${tableName}-${index}-${columnKey}`}
            columnSchema={columnSchema}
            cellSchema={schema.properties[columnKey]}
            levelIndex={level ? levelIndex + 1 : levelIndex}
            funds={funds}
            fundKeys={fundKeys}
            getValue={fundKey => funds?.[fundKey]?.fund_data?.[tableName]?.[0] || {}}
            onFundChange={(fundKey, value) => handleCellChange(columnKey, value, fundKey)}
            readOnly={Boolean(columnSchema?.disabled)}
            cellRefs={cellRefs}
            getCellKey={fundKey => `${keyPrefix}-${columnKey}-${fundKey}`}
            onCellKeyDown={onCellKeyDown}
            tableName={tableName}
            fieldName={columnKey}
            hasDrillDownFeature={schema.properties[columnKey]?.has_drill_down_feature}
            symbol={schema.properties[columnKey]?.symbol}
          />
        ))}

      {children &&
        children.map(childQuestion => (
          <Fragment key={`${keyPrefix}-${childQuestion.level ?? childQuestion.name}`}>
            <MultiFundQuestion
              question={childQuestion}
              schema={schema}
              funds={funds}
              fundKeys={fundKeys}
              tableName={tableName}
              levelIndex={levelIndex + 1}
              handleCellChange={handleCellChange}
              cellRefs={cellRefs}
              onCellKeyDown={onCellKeyDown}
            />
          </Fragment>
        ))}
    </>
  );
};

const SingleFundQuestion = ({
  question,
  schema,
  data,
  levelIndex,
  handleCellChange,
  cellRefs,
  onCellKeyDown,
  fund,
  tableName
}) => {
  const { columns, children, level, name } = question;
  const columnEntries = getColumnEntries(columns);
  const keyPrefix = question.level || question.name;
  const fundId = data?.fund_id || fund?.fund_id;
  const fundName = data?.fund_name || fund?.fund_name;

  return (
    <>
      <SingleFundHeaderRow levelIndex={levelIndex} level={level} name={name} />

      {!children &&
        columnEntries.map(([columnKey, columnSchema]) => (
          <SingleFundRow
            key={`${keyPrefix}-${columnKey}`}
            columnSchema={columnSchema}
            cellSchema={schema.properties[columnKey]}
            levelIndex={level ? levelIndex + 1 : levelIndex}
            value={resolveColumnValue(data, columnKey)}
            error={data?.errors?.[columnKey]}
            onChange={value => handleCellChange(columnKey, value)}
            readOnly={Boolean(columnSchema?.disabled)}
            cellRefs={cellRefs}
            cellKey={`${keyPrefix}-${columnKey}`}
            onCellKeyDown={onCellKeyDown}
            fundId={fundId}
            fundName={fundName}
            tableName={tableName}
            fieldName={columnKey}
            tableId={resolveColumnValue(data, 'id')}
            reportId={resolveColumnValue(data, 'report_id')}
            hasDrillDownFeature={schema.properties[columnKey]?.has_drill_down_feature}
            isFromStaticTemplate={data?.static_template_fields?.includes(columnKey)}
            symbol={schema.properties[columnKey]?.symbol}
          />
        ))}

      {children &&
        children.map(childQuestion => (
          <Fragment key={`${keyPrefix}-${childQuestion.level ?? childQuestion.name}`}>
            <SingleFundQuestion
              question={childQuestion}
              schema={schema}
              data={data}
              levelIndex={levelIndex + 1}
              handleCellChange={handleCellChange}
              cellRefs={cellRefs}
              onCellKeyDown={onCellKeyDown}
              fund={fund}
              tableName={tableName}
            />
          </Fragment>
        ))}
    </>
  );
};

export const SimpleTable = ({
  schema,
  fundId,
  isMultiFunds,
  funds,
  tableName,
  onChangeFund,
  name,
  questionLevels = [],
  showQuestionHeader = true,
  isQuestionOpen = true,
  onToggleQuestion
}) => {
  const fundKeys = useMemo(() => Object.keys(funds || {}), [funds]);
  const { cellRefs, registerCell, onCellKeyDown } = useCellNavigation({ isMultiFunds, fundKeys });

  const resolveRow = useCallback(
    targetFundId => funds?.[targetFundId]?.fund_data?.[tableName]?.[0] ?? {},
    [funds, tableName]
  );

  const handleCellChange = useCallback(
    (columnKey, value, targetFundId) => {
      const resolvedFundId = isMultiFunds ? targetFundId : fundId;
      if (resolvedFundId === undefined || resolvedFundId === null) {
        return;
      }

      const currentRow = resolveRow(resolvedFundId);
      const nextRow = [
        {
          ...currentRow,
          [columnKey]: value
        }
      ];

      onChangeFund(tableName, nextRow, targetFundId);
    },
    [fundId, isMultiFunds, onChangeFund, resolveRow, tableName]
  );

  const { questionNumber, questionName } = parseQuestionDisplayName(name);

  if (isMultiFunds) {
    const isWidthFit =
      fundKeys.length > 3 && window.innerWidth < fundKeys.length * 260 + LEFT_SECTION_WIDTH;
    return (
      <Flex
        id={`formpf-simple-table-${idCreator(tableName)}-multi`}
        direction="column"
        w={isWidthFit ? 'fit-content' : 'auto'}
        bd={`1px solid ${BORDER_COLOR}`}
      >
        {showQuestionHeader && (
          <QuestionHeader
            id={`formpf-simple-table-header-${idCreator(tableName)}-multi`}
            name={questionName}
            number={questionNumber}
            fundKeys={fundKeys}
            funds={funds}
            isMultiFunds={isMultiFunds}
            isOpen={isQuestionOpen}
            onToggle={onToggleQuestion}
          />
        )}

        {isQuestionOpen &&
          questionLevels.map((question, index) => (
            <Fragment key={`${tableName}-${index}-multi-view`}>
              <MultiFundQuestion
                question={question}
                schema={schema}
                funds={funds}
                fundKeys={fundKeys}
                tableName={tableName}
                levelIndex={0}
                handleCellChange={handleCellChange}
                cellRefs={{ current: cellRefs, register: registerCell }}
                onCellKeyDown={onCellKeyDown}
              />
            </Fragment>
          ))}
      </Flex>
    );
  }

  return (
    <Flex
      id={`formpf-simple-table-${idCreator(tableName)}-single`}
      direction="column"
      bd={`1px solid ${BORDER_COLOR}`}
    >
      {showQuestionHeader && (
        <QuestionHeader
          id={`formpf-simple-table-header-${idCreator(tableName)}-single`}
          number={questionNumber}
          name={questionName}
          isMultiFunds={isMultiFunds}
          isOpen={isQuestionOpen}
          onToggle={onToggleQuestion}
        />
      )}
      {isQuestionOpen &&
        questionLevels.map((question, index) => (
          <Fragment key={`${tableName}-${index}`}>
            <SingleFundQuestion
              question={question}
              schema={schema}
              data={funds?.[fundId]?.fund_data?.[tableName]?.[0]}
              levelIndex={!question.level && !question.name ? -1 : 0}
              handleCellChange={handleCellChange}
              cellRefs={{ current: cellRefs, register: registerCell }}
              onCellKeyDown={onCellKeyDown}
              fund={funds?.[fundId]}
              tableName={tableName}
            />
          </Fragment>
        ))}
    </Flex>
  );
};
